// ═══════════════════════════════════════════════════════════════════════════
//  HoldButton — fires only after a deliberate press-and-hold.
//  Mobile execution, blueprint Phase 7 (God 2026-09-04).
//
//  A tap is something a pocket can do. A sustained hold is not. This exists
//  because the fastest surface to send an order from is now also the one most
//  likely to be touched by accident — a mis-scroll onto a button, a shared or
//  unlocked device, a phone in a pocket. The console already removed a header
//  toggle on 2026-09-02 for the same reason: "a one-click route to LIVE VIEW
//  sat inches from KILL ALL".
//
//  The mechanics that matter:
//   * The progress is REAL TIME, measured against a wall clock — not a CSS
//     transition that could desync from the actual timer. What the bar shows is
//     what the clock has counted.
//   * The clock is driven by setInterval, NOT requestAnimationFrame. rAF is
//     suspended outright when the page is not being painted (measured: 0 frames
//     in 1s on an occluded window), which stalls a genuine hold indefinitely —
//     safe, since it cannot fire, but the button simply stops responding and
//     the user has no idea why. iOS low-power mode and an incoming-call overlay
//     put a real phone in the same state. 40ms is 25fps: smooth enough for a
//     900ms fill and beholden to nothing but the clock.
//   * Releasing early cancels and RESETS to zero. Progress does not accumulate
//     across attempts, or three impatient taps become a fire.
//   * pointercancel and pointerleave cancel too: a hold that turns into a
//     scroll must not complete.
//   * It fires ONCE per hold (firedRef), so a slow pointerup after completion
//     cannot send a second order.
//   * setPointerCapture keeps the gesture bound to this element, so a finger
//     that drifts a few pixels does not silently abort.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface HoldButtonProps {
  onConfirm: () => void;
  /** How long the press must be sustained. */
  holdMs?: number;
  disabled?: boolean;
  /** 'neg' paints the destructive/real-money treatment. */
  tone?: 'pos' | 'neg' | 'info';
  children: React.ReactNode;
  /** Shown while holding, in place of children. */
  holdingLabel?: string;
  style?: React.CSSProperties;
}

export const HoldButton: React.FC<HoldButtonProps> = ({
  onConfirm, holdMs = 900, disabled = false, tone = 'info',
  children, holdingLabel = 'HOLD…', style,
}) => {
  const [progress, setProgress] = useState(0);   // 0..1
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef(0);
  const firedRef = useRef(false);

  const stop = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    startRef.current = 0;
    setProgress(0);
  }, []);

  useEffect(() => stop, [stop]);

  const tick = useCallback(() => {
    if (!startRef.current) return;
    const p = Math.min(1, (performance.now() - startRef.current) / holdMs);
    setProgress(p);
    if (p >= 1) {
      if (!firedRef.current) {
        firedRef.current = true;
        // A short buzz at the moment of commit, where supported. It is the only
        // confirmation a thumb gets that the hold actually completed.
        try { navigator.vibrate?.(18); } catch { /* unsupported */ }
        onConfirm();
      }
      stop();
      return;
    }
  }, [holdMs, onConfirm, stop]);

  const begin = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    if (disabled) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
    firedRef.current = false;
    startRef.current = performance.now();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(tick, 40);
    tick();                       // paint the first step without waiting a frame
  }, [disabled, tick]);

  const colour = tone === 'neg' ? 'var(--jc-neg)'
               : tone === 'pos' ? 'var(--jc-pos)'
               : 'var(--jc-info)';
  const holding = progress > 0;

  return (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={begin}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onContextMenu={e => e.preventDefault()}   // long-press menu would eat the gesture
      style={{
        position: 'relative', overflow: 'hidden',
        minHeight: 52, padding: '0 18px', width: '100%',
        borderRadius: 'var(--jc-r-md)',
        border: `1px solid ${disabled ? 'var(--jc-hairline)' : colour + '66'}`,
        background: disabled ? 'var(--jc-surface)' : `color-mix(in srgb, ${colour} 12%, transparent)`,
        color: disabled ? 'var(--jc-text-mute)' : colour,
        font: '800 12px/1 var(--jc-ui)', letterSpacing: 1.2, textTransform: 'uppercase',
        cursor: disabled ? 'not-allowed' : 'pointer',
        touchAction: 'none',                    // the hold must not become a scroll
        userSelect: 'none', WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        boxShadow: holding ? `0 0 22px ${colour}44` : 'none',
        transition: 'box-shadow 160ms ease',
        ...style,
      }}>
      {/* Fill grows left→right with the actual elapsed hold. */}
      <span aria-hidden style={{
        position: 'absolute', inset: 0, transformOrigin: 'left center',
        transform: `scaleX(${progress})`,
        background: `linear-gradient(90deg, ${colour}2e, ${colour}55)`,
        pointerEvents: 'none',
      }} />
      <span style={{ position: 'relative', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: 8 }}>
        {holding ? `${holdingLabel} ${Math.round(progress * 100)}%` : children}
      </span>
    </button>
  );
};

export default HoldButton;
